import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  X, Calendar, Clock, MapPin, User, Briefcase, DollarSign,
  Edit, Trash2, Bell, Send, Phone, Mail, Package, Loader2, Receipt, FileCheck,
  CreditCard, Car, Percent, Navigation, ExternalLink, Volume2, Star, Copy, Check,
  Tag, Layers, Plus, Pencil, Wrench, Repeat,
} from "lucide-react";
import { format } from "date-fns";
import { getDisplayAddress, getGoogleMapsUrl } from "../../utils/job-form-utils";
import { formatJobScheduleDisplay } from "../../utils/dateUtils";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import CallRecordingsModal from "./CallRecordingsModal";
import JobAttachments from "./JobAttachments";
import ThumbtackMessages from "./ThumbtackMessages";
import { copyTextAsync } from "../../utils/clipboard";
import InvoiceFormDialog from "../invoices/InvoiceFormDialog";
import QuickAssignTech from "./QuickAssignTech";
import { useTheme } from "../../contexts/ThemeContext";

const STATUS_COLORS = {
  scheduled: "#3B82F6",
  in_progress: "#F59E0B",
  deposit_collected: "#14B8A6",
  completed: "#22C55E",
  completed_pending_payment: "#F59E0B",
  cancelled: "#EF4444",
};

const JobPreviewModal = ({
  job,
  onClose,
  onEdit,
  onDelete,
  onSendReminder,
  onSendToTech,
  onSendReviewRequest,
  onNewJobForCustomer,
  onOpenCustomer,
  sendingToTech,
  sendingReview,
  paymentTypes = [],
  onRename,
  // { teamMembers, onAssigned } — renders the quick technician assignment bar
  // under the header (used by the map-focused details modal).
  quickAssign = null,
}) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [showRecordings, setShowRecordings] = useState(false);
  const [ticketCopied, setTicketCopied] = useState(false);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  // Inline invoice/estimate creation — the form opens OVER this modal so the
  // user lands right back here when done (never navigates away).
  const [docCreate, setDocCreate] = useState(null); // "invoice" | "estimate" | null
  const [invRef, setInvRef] = useState(null); // { customers, businessName }

  const openDocForm = (type) => {
    setDocCreate(type);
    if (invRef) return;
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([
      axios.get(`${API}/customers`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/settings/business`, { headers }).catch(() => ({ data: {} })),
    ]).then(([custRes, bizRes]) => {
      setInvRef({
        customers: Array.isArray(custRes.data) ? custRes.data : [],
        businessName: bizRes.data?.business_name || "",
      });
    });
  };

  const docPrefill = useMemo(() => {
    if (!docCreate || !job) return null;
    const items = [];
    if (job.service_name || job.title) items.push({ description: job.service_name || job.title, quantity: 1, unit_price: job.total_charged || job.price || 0, discount: 0 });
    (job.parts || []).forEach((p) => items.push({ description: `Part: ${p.name}`, quantity: 1, unit_price: p.cost || 0, discount: 0 }));
    if (!items.length) items.push({ description: "", quantity: 1, unit_price: 0, discount: 0 });
    return {
      customer_id: job.customer_id || "",
      job_id: job.job_id,
      store_location_id: job.store_location_id || null,
      items,
      docType: docCreate,
    };
  }, [docCreate, job]);

  const startRename = () => {
    setTitleDraft(job?.title || "");
    setEditingTitle(true);
  };
  const saveRename = async () => {
    const t = titleDraft.trim();
    setEditingTitle(false);
    if (t && t !== job?.title) await onRename?.(job, t);
  };

  const copyTicket = async () => {
    setTicketLoading(true);
    // IMPORTANT: do NOT `await` anything before invoking copyTextAsync —
    // iOS Safari requires the clipboard call to start inside the user
    // gesture. copyTextAsync passes the fetch promise into ClipboardItem
    // so iOS keeps the gesture context alive until axios resolves.
    const token = localStorage.getItem("token");
    let maskingWarning = null;
    try {
      const ok = await copyTextAsync(async () => {
        const res = await axios.get(`${API}/jobs/${job.job_id}/ticket-text`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        maskingWarning = res.data?.masking_warning || null;
        return res.data?.ticket_text || "";
      });
      if (!ok) throw new Error("clipboard unavailable");
      setTicketCopied(true);
      if (maskingWarning) toast.warning(maskingWarning, { duration: 9000 });
      toast.success("Ticket copied to clipboard");
      setTimeout(() => setTicketCopied(false), 2000);
    } catch {
      toast.error("Failed to copy ticket");
    } finally {
      setTicketLoading(false);
    }
  };

  // Fetch invoices/estimates linked to this job (server filters by job_id)
  const [linkedDocs, setLinkedDocs] = useState([]);
  const [linkedLoading, setLinkedLoading] = useState(false);
  const fetchLinkedDocs = useCallback(() => {
    if (!job?.job_id) return;
    setLinkedLoading(true);
    const token = localStorage.getItem("token");
    axios.get(`${API}/invoices?job_id=${job.job_id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then((r) => setLinkedDocs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLinkedDocs([]))
      .finally(() => setLinkedLoading(false));
  }, [job?.job_id]);
  useEffect(() => { fetchLinkedDocs(); }, [fetchLinkedDocs]);

  if (!job) return null;

  const color = STATUS_COLORS[job.status] || "#6B7280";
  const isCompleted = job.status === "completed" || job.status === "completed_pending_payment";

  // Build payment type name lookup
  const ptNameMap = {};
  paymentTypes.forEach(pt => { ptNameMap[pt.payment_id] = pt.name; });

  // Use backend-calculated financial data (populated by GET /api/jobs for completed jobs)
  const hasFinancials = isCompleted && job.total_charged > 0 && (
    job.source_commission_amount > 0 || job.technician_commission_amount > 0 ||
    job.processing_fees > 0 || job.net_profit != null
  );

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="job-preview-modal"
    >
      <div
        className="relative bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg border border-border my-0 sm:my-8"
        style={{ boxShadow: "0 -4px 32px rgba(0,0,0,0.18)", maxHeight: "calc(100dvh - 80px)", marginTop: "auto", marginBottom: "80px" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile handle */}
        <div className="sm:hidden flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/20" />
        </div>

        {/* Header */}
        <div className="sticky top-0 z-10 bg-background rounded-t-2xl px-5 py-4 flex items-start justify-between gap-3 border-b border-border">
          <div className="flex-1 min-w-0">
            {editingTitle ? (
              <div className="flex items-center gap-1.5">
                <input
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveRename();
                    if (e.key === "Escape") setEditingTitle(false);
                  }}
                  className="flex-1 min-w-0 h-8 px-2 rounded-md bg-theme-surface border border-border/60 text-base font-semibold text-foreground outline-none focus:border-[#FFCC00]/60"
                  placeholder="Job title"
                  data-testid="preview-title-input"
                />
                <button type="button" onClick={saveRename} className="p-1 rounded-md text-emerald-500 hover:bg-emerald-500/10" title="Save" data-testid="preview-title-save">
                  <Check className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => setEditingTitle(false)} className="p-1 rounded-md text-muted-foreground hover:bg-muted" title="Cancel" data-testid="preview-title-cancel">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 min-w-0">
                <h2 className="text-lg font-semibold text-foreground truncate" data-testid="preview-title">
                  {job.title || "Service Job"}
                </h2>
                {onRename && (
                  <button
                    type="button"
                    onClick={startRename}
                    className="p-1 rounded-md text-muted-foreground/50 hover:text-foreground hover:bg-muted flex-shrink-0"
                    title="Rename job"
                    data-testid="preview-rename-job"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-0.5 font-mono">
              {job.job_display_id || `#${job.job_id?.slice(0, 8)}`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge
              className="text-[11px] px-2.5 py-0.5 border capitalize"
              style={{ background: `${color}12`, color, borderColor: `${color}30` }}
              data-testid="preview-status"
            >
              {job.status?.replace("_", " ")}
            </Badge>
            {job.is_recurring && (
              <Badge
                className="text-[11px] px-2.5 py-0.5 border"
                style={{ background: "#06b6d412", color: "#06b6d4", borderColor: "#06b6d430" }}
                data-testid="preview-recurring-badge"
              >
                <Repeat className="w-3 h-3 mr-1" />
                Every {job.recurring_interval_value} {job.recurring_interval_unit}
              </Badge>
            )}
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted"
              data-testid="close-job-preview"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Quick technician assignment — saves instantly, assignment fields only */}
        {quickAssign && (
          <div className="px-5 py-2.5 border-b border-border bg-theme-surface/40 flex items-center gap-2.5" data-testid="preview-quick-assign-bar">
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Wrench className="w-3.5 h-3.5 text-[#FFCC00]" />
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Technician</span>
            </div>
            <QuickAssignTech
              job={job}
              teamMembers={quickAssign.teamMembers}
              onAssigned={quickAssign.onAssigned}
              className="flex-1 min-w-0"
            />
          </div>
        )}

        {/* Scrollable body */}
        <div className="overflow-y-auto" style={{ maxHeight: "calc(100dvh - 80px - 64px - 120px)" }}>
          <div className="p-5 space-y-4">
            {onOpenCustomer && job.customer_id ? (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-amber-500" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Customer</p>
                  <button
                    type="button"
                    onClick={() => onOpenCustomer(job)}
                    className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline text-left"
                    data-testid="preview-customer-link"
                  >
                    {job.customer_name || "View customer"}
                  </button>
                </div>
              </div>
            ) : (
              <InfoRow icon={<User className="w-4 h-4 text-amber-500" />} bg="bg-amber-500/10" label="Customer" value={job.customer_name || "\u2014"} />
            )}

            {/* Customer Contact - Phone & Email */}
            {(job.customer_phone || job.customer_phone2 || job.customer_email) && (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-rose-500/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-rose-500" />
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  {job.customer_phone && (
                    <span className="inline-flex items-center gap-1.5">
                      <a
                        href={`tel:${job.customer_phone}${job.customer_phone_ext ? `,,${job.customer_phone_ext}` : ""}`}
                        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline transition-colors"
                        data-testid="preview-customer-phone"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {job.customer_phone}
                      </a>
                      {job.customer_phone_ext && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 hover:bg-blue-500/20"
                          title="Copy extension"
                          onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(job.customer_phone_ext); toast.success("Extension copied"); }}
                          data-testid="preview-phone-ext"
                        >
                          ext. {job.customer_phone_ext}
                          <Copy className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </span>
                  )}
                  {job.customer_phone2 && (
                    <span className="inline-flex items-center gap-1.5">
                      <a
                        href={`tel:${job.customer_phone2}${job.customer_phone2_ext ? `,,${job.customer_phone2_ext}` : ""}`}
                        className="text-sm text-muted-foreground hover:text-foreground hover:underline transition-colors"
                        data-testid="preview-customer-phone2"
                        onClick={(e) => e.stopPropagation()}
                        title="Secondary phone"
                      >
                        {job.customer_phone2} <span className="text-[10px] uppercase">(2nd)</span>
                      </a>
                      {job.customer_phone2_ext && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground border border-border/60 hover:bg-muted/70"
                          title="Copy extension"
                          onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(job.customer_phone2_ext); toast.success("Extension copied"); }}
                          data-testid="preview-phone2-ext"
                        >
                          ext. {job.customer_phone2_ext}
                          <Copy className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </span>
                  )}
                  {job.customer_email && (
                    <a
                      href={`mailto:${job.customer_email}`}
                      className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="preview-customer-email"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Mail className="w-3 h-3" /> {job.customer_email}
                    </a>
                  )}
                </div>
              </div>
            )}

            {(job.service_name || (job.service_type && job.service_type !== "other")) && (
              <div className="flex items-center gap-3" data-testid="preview-service-type">
                <div className="w-9 h-9 rounded-xl bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                  <Wrench className="w-4 h-4 text-orange-500" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Service Type</p>
                  <p className="text-sm font-medium text-foreground capitalize">{job.service_name || job.service_type}</p>
                </div>
              </div>
            )}

            {job.scheduled_date && (
              <InfoRow icon={<Calendar className="w-4 h-4 text-blue-500" />} bg="bg-blue-500/10" label="Date"
                value={format(new Date(job.scheduled_date), "EEEE, MMMM d, yyyy")} />
            )}
            {(formatJobScheduleDisplay(job)) && (
              <InfoRow icon={<Clock className="w-4 h-4 text-purple-500" />} bg="bg-purple-500/10" label="Time" value={formatJobScheduleDisplay(job)} />
            )}

            {job.customer_address && (
              <div className="space-y-1.5">
                <InfoRow icon={<MapPin className="w-4 h-4 text-emerald-500" />} bg="bg-emerald-500/10" label="Address"
                  value={getDisplayAddress(job)} />
                {getGoogleMapsUrl(job) && (
                  <div className="flex items-center gap-2 pl-10">
                    <a href={getGoogleMapsUrl(job)} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-colors"
                      data-testid="preview-google-maps-btn">
                      <Navigation className="w-3 h-3" /> Google Maps <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                    <a href={`https://maps.apple.com/?q=${encodeURIComponent(getDisplayAddress(job))}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-gray-500/10 text-gray-600 dark:text-gray-400 hover:bg-gray-500/20 transition-colors"
                      data-testid="preview-apple-maps-btn">
                      <Navigation className="w-3 h-3" /> Apple Maps <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  </div>
                )}
              </div>
            )}

            {job.assigned_to_name && (
              <InfoRow icon={<Briefcase className="w-4 h-4 text-cyan-500" />} bg="bg-cyan-500/10"
                label={(job.additional_technician_names || []).length > 0 ? "Technicians" : "Technician"}
                value={[job.assigned_to_name, ...(job.additional_technician_names || [])].filter(Boolean).join(", ")} />
            )}

            {/* Vehicle */}
            {(job.vehicle_year || job.vehicle_make || job.vehicle_model) && (
              <div className="rounded-xl bg-muted/50 border border-border p-3">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><Car className="w-3 h-3" /> Vehicle</p>
                <p className="text-sm font-medium text-foreground">
                  {[job.vehicle_year, job.vehicle_make, job.vehicle_model].filter(Boolean).join(" ")}
                </p>
                {job.vehicle_vin && <p className="text-xs text-muted-foreground font-mono mt-1">VIN: {job.vehicle_vin}</p>}
              </div>
            )}

            {/* Store-specific fields */}
            {job.store_location_name && (
              <InfoRow icon={<MapPin className="w-4 h-4 text-blue-500" />} bg="bg-blue-500/10" label="Store Location" value={job.store_location_name} />
            )}
            {job.ticket_number && (
              <InfoRow icon={<Tag className="w-4 h-4 text-indigo-500" />} bg="bg-indigo-500/10" label="Ticket #" value={job.ticket_number} />
            )}
            {job.store_category_name && (
              <InfoRow icon={<Layers className="w-4 h-4 text-teal-500" />} bg="bg-teal-500/10" label="Category"
                value={[job.store_category_name, job.store_sub_category_name].filter(Boolean).join(" > ")} />
            )}
            {job.walk_in !== undefined && job.walk_in !== null && job.store_location_id && (
              <InfoRow icon={<User className="w-4 h-4 text-orange-500" />} bg="bg-orange-500/10" label="Type" value={job.walk_in ? "Walk-In" : "Appointment"} />
            )}
            {(job.all_keys_lost || job.vehicle_running === false || job.has_key === false) && (
              <div className="flex flex-wrap gap-2">
                {job.all_keys_lost && <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-500 font-medium">All Keys Lost</span>}
                {job.vehicle_running === false && <span className="text-[11px] px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-600 font-medium">Vehicle Not Running</span>}
                {job.has_key === false && <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-500 font-medium">No Key</span>}
              </div>
            )}

            {/* Notes */}
            {job.notes && (
              <div className="rounded-xl bg-muted/50 border border-border p-3">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Notes</p>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{job.notes}</p>
              </div>
            )}

            {/* Thumbtack two-way messaging (leads synced from Thumbtack) */}
            {job.thumbtack_negotiation_id && <ThumbtackMessages job={job} />}

            {/* Attachments — photos / PDFs on this job */}
            <JobAttachments jobId={job.job_id} />

            {/* Payment Methods */}
            {isCompleted && job.payments?.length > 0 && (
              <div className="rounded-xl bg-muted/50 border border-border p-3">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                  <CreditCard className="w-3 h-3" /> Payment Method
                </p>
                <div className="space-y-1.5">
                  {job.payments.map((p, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-foreground">{ptNameMap[p.payment_type_id] || p.payment_type_id || "Payment"}</span>
                      <span className="text-foreground font-medium">${(p.amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Parts */}
            {job.parts?.length > 0 && (
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Package className="w-3 h-3" /> Parts
                </p>
                <div className="space-y-1.5">
                  {job.parts.map((p, i) => (
                    <div key={i} className="flex justify-between text-sm px-3 py-2 rounded-lg bg-muted/50 border border-border">
                      <div>
                        <span className="text-foreground">{p.name}</span>
                        {p.provider && <span className="text-muted-foreground text-xs ml-2">({p.provider})</span>}
                      </div>
                      <span className="text-muted-foreground">${(p.cost || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Call Recordings */}
            {(job.call_history?.length > 0 || job.call_mask_pin) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRecordings(true)}
                className="w-full border-[#FFCC00]/30 text-[#FFCC00] hover:bg-[#FFCC00]/10 justify-center"
                data-testid="view-recordings-btn"
              >
                <Volume2 className="w-3.5 h-3.5 mr-1.5" />
                View Call Recordings
                {job.call_history?.length > 0 && (
                  <Badge className="ml-2 bg-[#FFCC00]/20 text-[#FFCC00] border-[#FFCC00]/30 text-[10px] px-1.5 py-0">
                    {job.call_history.length}
                  </Badge>
                )}
              </Button>
            )}

            {/* Financial Breakdown — Completed jobs with backend-calculated data */}
            {hasFinancials && (
              <div className="rounded-xl bg-muted/40 border border-border p-4" data-testid="financial-breakdown">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Percent className="w-3 h-3" /> Financial Breakdown
                </p>
                <div className="space-y-1.5 text-sm">
                  <FinRow label="Total Charged" value={job.total_charged} />

                  {job.company_parts_cost > 0 && <FinRow label="Company Parts" value={-job.company_parts_cost} negative />}
                  {job.tech_parts_cost > 0 && <FinRow label="Tech Parts (reimb.)" value={-job.tech_parts_cost} negative />}

                  {job.source_commission_amount > 0 && (
                    <FinRow
                      label={`Source Commission${job.source_name ? ` (${job.source_name})` : ""}`}
                      value={-job.source_commission_amount}
                      negative
                    />
                  )}
                  {job.source_extra_fee > 0 && <FinRow label="Source Extra Fee" value={-job.source_extra_fee} negative />}

                  {job.technician_commission_amount > 0 && (
                    <FinRow
                      label={`Tech Commission${job.assigned_to_name ? ` (${job.assigned_to_name})` : ""}`}
                      value={-job.technician_commission_amount}
                      negative
                    />
                  )}

                  {job.processing_fees > 0 && <FinRow label="Processing Fees" value={-job.processing_fees} warn />}

                  {job.primary_tech_payout > 0 && (
                    <div className="flex justify-between pl-3 text-xs">
                      <span className="text-muted-foreground">Tech Payout</span>
                      <span className="text-orange-600 dark:text-orange-400">${job.primary_tech_payout.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="flex justify-between pt-2 border-t border-border">
                    <span className="text-foreground font-medium">Company Profit</span>
                    <span className={`font-bold ${job.net_profit >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                      ${(job.net_profit || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Simple pricing for non-completed or no-financial jobs */}
            {!hasFinancials && (job.total_charged > 0 || job.price > 0) && (
              <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/15 p-3">
                <p className="text-[10px] text-emerald-700 dark:text-emerald-400 uppercase tracking-wider mb-1">
                  {isCompleted ? "Total Charged" : "Price"}
                </p>
                <p className="text-xl font-bold text-emerald-700 dark:text-emerald-400">
                  ${(job.total_charged || job.price || 0).toFixed(2)}
                </p>
              </div>
            )}

            {/* Linked Documents (invoices + estimates with this job_id) */}
            {(linkedLoading || linkedDocs.length > 0) && (
              <div className="rounded-xl bg-card border border-border p-3" data-testid="job-linked-documents">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <FileCheck className="w-3 h-3" /> Linked Documents
                  {!linkedLoading && <span className="text-foreground">· {linkedDocs.length}</span>}
                </p>
                {linkedLoading ? (
                  <p className="text-xs text-muted-foreground">Loading…</p>
                ) : (
                  <div className="space-y-1.5">
                    {linkedDocs.map((d) => {
                      const isInvoice = (d.document_type || "invoice") === "invoice";
                      return (
                        <button
                          key={d.invoice_id}
                          type="button"
                          onClick={() => { onClose(); navigate(`/dashboard/invoices?open=${d.invoice_id}`); }}
                          className="w-full flex items-center justify-between px-2.5 py-2 rounded-md hover:bg-muted/40 transition-colors text-left"
                          data-testid={`linked-doc-${d.invoice_id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {isInvoice
                              ? <Receipt className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                              : <FileCheck className="w-3.5 h-3.5 text-orange-500 shrink-0" />}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {isInvoice ? "Invoice" : "Estimate"}{" "}
                                <span className="text-muted-foreground">#{d.invoice_number || d.estimate_number || d.invoice_id?.slice(0, 8)}</span>
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                {d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}
                                {d.status && ` · ${d.status.replace(/_/g, " ")}`}
                              </p>
                            </div>
                          </div>
                          <div className="text-right shrink-0 ml-2">
                            <p className="text-sm font-semibold text-foreground">${(d.total || 0).toFixed(2)}</p>
                            {(d.amount_paid || 0) > 0 && d.status !== "paid" && (
                              <p className="text-[10px] text-amber-500" data-testid={`linked-doc-balance-${d.invoice_id}`}>
                                Paid ${(d.amount_paid || 0).toFixed(2)} · Bal ${Math.max((d.total || 0) - (d.amount_paid || 0), 0).toFixed(2)}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer actions — always visible above mobile nav */}
        <div className="sticky bottom-0 bg-background border-t border-border p-4 rounded-b-2xl space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => { onEdit(job); onClose(); }}
              className="bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90 flex-1 sm:flex-none" data-testid="preview-edit-btn">
              <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
            </Button>
            {onNewJobForCustomer && (
              <Button size="sm" variant="outline" onClick={() => { onNewJobForCustomer(job); onClose(); }}
                className="border-blue-500/30 text-blue-500 hover:bg-blue-500/10" data-testid="preview-new-job-same-customer-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New Job for Customer
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={copyTicket} disabled={ticketLoading}
              className="border-[#FFCC00]/30 text-[#FFCC00] hover:bg-[#FFCC00]/10" data-testid="preview-copy-ticket-btn">
              {ticketLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : ticketCopied ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
              {ticketCopied ? "Copied!" : "Copy Ticket"}
            </Button>
            {job.status === "scheduled" && (
              <Button size="sm" variant="outline" onClick={() => onSendReminder(job)}
                className="border-blue-500/30 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10" data-testid="preview-reminder-btn">
                <Bell className="w-3.5 h-3.5 mr-1.5" /> Remind
              </Button>
            )}
            {job.assigned_to && job.assignment_status !== "accepted" && (
              <Button size="sm" variant="outline" onClick={() => onSendToTech(job)}
                disabled={sendingToTech === job.job_id}
                className="border-green-500/30 text-green-600 dark:text-green-400 hover:bg-green-500/10" data-testid="preview-send-tech-btn">
                {sendingToTech === job.job_id
                  ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Send to Tech
              </Button>
            )}
            {isCompleted && onSendReviewRequest && (
              <Button size="sm" variant="outline" onClick={() => onSendReviewRequest(job)}
                disabled={sendingReview === job.job_id}
                className="border-[#FFCC00]/30 text-[#FFCC00] hover:bg-[#FFCC00]/10" data-testid="preview-request-review-btn">
                {sendingReview === job.job_id
                  ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <Star className="w-3.5 h-3.5 mr-1.5" />}
                Request Review
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => { onDelete(job); onClose(); }}
              className="text-red-600 dark:text-red-400 hover:bg-red-500/10 ml-auto" data-testid="preview-delete-btn">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
          {job.customer_id && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline"
                onClick={() => openDocForm("invoice")}
                className="flex-1 border-blue-500/20 text-blue-700 dark:text-blue-400 hover:bg-blue-500/10 text-xs"
                data-testid="preview-create-invoice-btn">
                <Receipt className="w-3.5 h-3.5 mr-1.5" /> Create Invoice
              </Button>
              <Button size="sm" variant="outline"
                onClick={() => openDocForm("estimate")}
                className="flex-1 border-orange-500/20 text-orange-700 dark:text-orange-400 hover:bg-orange-500/10 text-xs"
                data-testid="preview-create-estimate-btn">
                <FileCheck className="w-3.5 h-3.5 mr-1.5" /> Create Estimate
              </Button>
            </div>
          )}
        </div>

        {/* Inline invoice/estimate creation — closes back to this modal */}
        {docCreate && (
          <InvoiceFormDialog
            open={!!docCreate}
            onClose={() => setDocCreate(null)}
            customers={invRef?.customers || []}
            isEstimate={docCreate === "estimate"}
            isDark={isDark}
            headers={(() => { const t = localStorage.getItem("token"); return t ? { Authorization: `Bearer ${t}` } : {}; })()}
            onCreated={() => { setDocCreate(null); fetchLinkedDocs(); }}
            paymentTypes={paymentTypes}
            jobPrefill={docPrefill}
            defaultCompanyName={invRef?.businessName || ""}
          />
        )}
      </div>

      {/* Call Recordings Modal */}
      {showRecordings && (
        <CallRecordingsModal
          jobId={job.job_id}
          jobTitle={job.title || job.job_display_id}
          onClose={() => setShowRecordings(false)}
        />
      )}
    </div>
  );
};

const FinRow = ({ label, value, negative, warn }) => (
  <div className="flex justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={
      negative ? "text-red-600 dark:text-red-400" :
      warn ? "text-orange-600 dark:text-orange-400" :
      "text-foreground font-medium"
    }>
      {value < 0 ? `-$${Math.abs(value).toFixed(2)}` : `$${value.toFixed(2)}`}
    </span>
  </div>
);

const InfoRow = ({ icon, bg, label, value }) => (
  <div className="flex items-center gap-3">
    <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
      {icon}
    </div>
    <div>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className="text-sm font-medium text-foreground">{value}</p>
    </div>
  </div>
);

export default JobPreviewModal;
